'use client'
import { memo, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import styled from 'styled-components'
import { AnimatePresence, motion, type Variants } from 'motion/react'
import { mq, vw, designGrid, getP40, getP15, getMargin100, getMargin15, getTransition } from '@/styles'
import { useTranslator } from '@/hooks'
import { SliderFlickity } from '../SliderFlickity'
import { Card } from './Card'

interface ISectionProjects {
  data: any,
  className?: string
  isRelated?: boolean
}

const getProjects = (data, pathname) => {
  if(!data) return []
  const splited = pathname.split('/project/')
  const _slug = splited[splited.length - 1]

  return data.filter(({ slug }) => slug !== _slug).map((project, idx) => <li key={idx + project.slug}><Card data={project} /></li>)
}

export const SectionProjects = memo(({ data, className = '', isRelated = false }:ISectionProjects) => {
  const { t } = useTranslator()
  const pathname = usePathname()
  const Projects = useMemo(() => getProjects(data, pathname), [data, pathname])

  return (
    <>
      <AnimatePresence>
        <Section variants={variants} initial='initial' animate={'animate'} exit='exit' className={className}>
          <h2>{t(`home.${isRelated ? 'section_relateds' : 'section_projects'}.title`)}</h2>
          <div className='p-block' dangerouslySetInnerHTML={{
            __html: t(`home.${isRelated ? 'section_relateds' : 'section_projects'}.description`) as string
          }} />
          <SliderFlickity className='slider--projects' reloadOnUpdate={true} options={{ cellAlign: 'left' }}>{Projects}</SliderFlickity>
        </Section>
      </AnimatePresence>
    </>
  )
})

const Section = styled(motion.section)`
  ${designGrid({})}
  overflow: hidden;
  padding: ${vw(50, 'mobile')} 0 ${vw(100, 'mobile')};
  opacity: 0;

  ${mq.greaterThan('tablet')} {
    padding: ${vw(100, 'desktop')} 0 ${vw(140, 'desktop')};
  }

  h2 {
    ${getP15()}
    text-align: left;
    grid-column: 1 / span 5;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 10;
    }

    &:last-of-type {
      ${getMargin15()}
    }
  }

  .p-block {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 0;
    grid-column: 1 / span 6;
    justify-content: flex-start;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 9;
    }
  }

  p {
    ${getP40()}
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 10;
    }

    &:last-of-type {
      margin-bottom: ${vw(60, 'mobile')};

      ${mq.greaterThan('tablet')} {
        ${getMargin100()}
      }
    }
  }

  .slider-wrapper {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }
  }

  li {
    border-radius: ${vw(6.96, 'mobile')};
    height: ${vw(454, 'mobile')};
    margin-right: ${vw(9.5, 'mobile')};
    overflow: hidden;
    width: ${vw(289, 'mobile')};

    ${mq.greaterThan('tablet')} {
      border-radius: ${vw(10, 'desktop')};
      height: ${vw(650, 'desktop')};
      margin-right: ${vw(15, 'desktop')};
      width: ${vw(414, 'desktop')};
    }
  }
`

const variants:Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [0.6, 0.2, 0.4, 1.5],
      delay: 2.8
    }
  }
}
